import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClipboardCheck, faClipboardQuestion, faUsers, faXmark } from "@fortawesome/free-solid-svg-icons";

import { nombreDeAlumno } from "../../../../utils/nombreCorto";
import { Clase } from "./calendarioApi";
import { bajoMinimo, dinero, fechaLarga, horaClase, maestraDe, subClase, tituloClase } from "./formato";
import { Contexto, IconoAsistencia, IconoCurso, estadoDeCuenta, useAlumnos } from "./piezas";

interface Props {
	c: Clase;
	ctx: Contexto;
	pasarLista: boolean;
	verHorario: boolean;
	onCerrar: () => void;
}

/** Panel al lado: la clase completa con su lista (asistencia de ese día, vencido que abre el Estado de Cuenta, kit pendiente). */
const PanelClase: React.FC<Props> = ({ c, ctx, pasarLista, verHorario, onCerrar }) => {
	const { alumnos, error } = useAlumnos(ctx.campusId, c);
	useEffect(() => {
		// Con un globito abierto, Escape cierra solo el globito.
		const tecla = (e: KeyboardEvent) => { if (e.key === "Escape" && !document.querySelector(".calcGlobo")) onCerrar(); };
		document.addEventListener("keydown", tecla);
		return () => document.removeEventListener("keydown", tecla);
	}, [onCerrar]);
	const conLista = !!c.lista?.pasada;
	const orden = (alumnos || []).slice().sort((a, b) => b.vencido - a.vencido || nombreDeAlumno(a.nombre, a.apellido).localeCompare(nombreDeAlumno(b.nombre, b.apellido), "es"));
	const hayKits = orden.some((a) => a.kit > 0);
	return (
		<aside className="calcPanel" role="dialog" aria-label={tituloClase(c)}>
			<div className="calcPanelCab">
				<IconoCurso c={c} />
				<div className="calcPanelTit">
					<h3>{tituloClase(c)}</h3>
					<p className="calcApagado">{subClase(c)}</p>
					<p>{fechaLarga(c.fecha)} · {horaClase(c)}</p>
					<p>{[c.aula?.nombre, c.edificio?.nombre].filter(Boolean).join(" · ") || "Sin Aula"}</p>
					<p className={maestraDe(c) ? "" : "calcOro"}>{maestraDe(c) || "Sin Maestra"}</p>
				</div>
				<button type="button" className="calcRedondo" aria-label="Cerrar" onClick={onCerrar}><FontAwesomeIcon icon={faXmark} /></button>
			</div>
			<div className="calcPanelCifras">
				<div><span>Alumnos</span><b className={bajoMinimo(c) ? "calcOro" : ""}>{c.activos}{c.aula?.cupo ? <small> de {c.aula.cupo}</small> : null}</b></div>
				<div><span>Asistencia</span><b>{!c.lista || c.activos < 1 ? "—" : c.lista.pasada ? <>{c.lista.presentes}<small> de {c.activos}</small></> : <FontAwesomeIcon icon={faClipboardQuestion} className="calcOro" title="Lista sin pasar" />}</b></div>
				<div><span>Saldo Vencido</span><b className={c.vencido?.alumnos ? "calcVencidoTxt" : ""}>{c.vencido?.alumnos ? dinero(c.vencido.total, ctx.countryId) : "—"}</b></div>
			</div>
			{pasarLista || (verHorario && c.codigo) ? (
				<div className="calcPanelAcciones">
					{pasarLista ? <Link className="btn btn-primary btn-sm rounded-pill" to={"/attendances?horario=" + c.horarioId}><FontAwesomeIcon icon={faClipboardCheck} />&nbsp;Pasar Lista</Link> : null}
					{verHorario && c.codigo ? <Link className="btn btn-outline-secondary btn-sm rounded-pill" to={`/schedules?horario=${encodeURIComponent(c.codigo)}&plantel=${ctx.campusId}`}><FontAwesomeIcon icon={faUsers} />&nbsp;Ver Horario</Link> : null}
				</div>
			) : null}
			<div className="calcPanelLista">
				{!alumnos ? <p className="calcGloboVacio">{error ? "No se pudo cargar la lista." : "Cargando…"}</p> : orden.length < 1 ? <p className="calcGloboVacio">Sin alumnos activos.</p> : (
					<table>
						<thead><tr><th className="estado">Hoy</th><th>Alumna</th><th className="der">Vencido</th>{hayKits ? <th className="der">Kit</th> : null}</tr></thead>
						<tbody>
							{orden.map((a) => (
								<tr key={a.matriculaId}>
									<td className="estado"><IconoAsistencia a={a} conLista={conLista} /></td>
									<td className={"nombre" + (conLista && a.asistencia === false ? " calcOro" : "")}>{nombreDeAlumno(a.nombre, a.apellido)}</td>
									<td className="der">{a.vencido > 0 ? <Link className="calcVencidoTxt" to={estadoDeCuenta(a.id, ctx.campusId)} title={(a.meses === 1 ? "1 mes" : a.meses + " meses") + " · Abrir Estado de Cuenta"}>{dinero(a.vencido, ctx.countryId)}</Link> : null}</td>
									{hayKits ? <td className="der calcOro">{a.kit > 0 ? dinero(a.kit, ctx.countryId) : null}</td> : null}
								</tr>
							))}
						</tbody>
					</table>
				)}
			</div>
		</aside>
	);
};

export default PanelClase;
